import { Modal, Steps, Form, Input, Button, message, Typography, Alert } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UsersService } from '@/services/api/users';
import { SystemAdminService } from '@/services/api/system-admin';

const { Title, Text } = Typography;

interface AddUserModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * Set by the admin interface: adds the user to this tenant via the
     * system-admin endpoints instead of the currently selected tenant (the
     * system admin is usually not a member of a freshly created tenant).
     */
    tenantId?: string;
    tenantName?: string;
}

export default function AddUserModal({ open, onClose, tenantId, tenantName }: AddUserModalProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [email, setEmail] = useState('');
    const [userExists, setUserExists] = useState<boolean | null>(null);
    const [userInTenant, setUserInTenant] = useState<boolean | null>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const invalidateUsers = () => {
        if (tenantId) {
            queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenant-users', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['system-admin', 'tenants'] });
        } else {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        }
    };

    const checkUserMutation = useMutation({
        mutationFn: (email: string) => tenantId
            ? SystemAdminService.getInstance().checkTenantUserExists(tenantId, { email })
            : UsersService.getInstance().checkUserExists({ email }),
        onSuccess: (data) => {
            setUserExists(data.exists);
            setUserInTenant(data.inTenant);

            if (data.inTenant) {
                message.info('Benutzer ist bereits Teil dieses Mandanten');
                return;
            }

            setCurrentStep(1);
        },
        onError: (error) => {
            message.error('Fehler beim Prüfen des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
        }
    });

    const addExistingUserMutation = useMutation({
        mutationFn: (email: string) => tenantId
            ? SystemAdminService.getInstance().addExistingUserToTenant(tenantId, { email })
            : UsersService.getInstance().addExistingUserToTenant({ email }),
        onSuccess: () => {
            message.success('Benutzer erfolgreich hinzugefügt');
            invalidateUsers();
            handleClose();
        },
        onError: (error) => {
            message.error('Fehler beim Hinzufügen des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
        }
    });

    const createNewUserMutation = useMutation({
        mutationFn: (userData: { email: string; displayName: string; password: string }) => tenantId
            ? SystemAdminService.getInstance().createNewUserInTenant(tenantId, userData)
            : UsersService.getInstance().createNewUserAndAddToTenant(userData),
        onSuccess: () => {
            message.success('Benutzer erfolgreich erstellt und hinzugefügt');
            invalidateUsers();
            handleClose();
        },
        onError: (error) => {
            message.error('Fehler beim Erstellen des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
        }
    });

    const handleEmailSubmit = () => {
        if (!email) {
            message.error('Bitte geben Sie eine E-Mail-Adresse ein');
            return;
        }
        checkUserMutation.mutate(email);
    };

    const handleAddExistingUser = () => {
        addExistingUserMutation.mutate(email);
    };

    const handleCreateNewUser = (values: { displayName: string; password: string }) => {
        createNewUserMutation.mutate({
            email,
            displayName: values.displayName,
            password: values.password
        });
    };

    const handleClose = () => {
        setCurrentStep(0);
        setEmail('');
        setUserExists(null);
        setUserInTenant(null);
        form.resetFields();
        onClose();
    };

    const renderStep0 = () => (
        <div>
            <Title level={4}>E-Mail-Adresse eingeben</Title>
            <Text>Geben Sie die E-Mail-Adresse des Benutzers ein, den Sie hinzufügen möchten.</Text>
            <Form style={{ marginTop: 24 }}>
                <Form.Item
                    name="email"
                    rules={[
                        { required: true, message: 'Bitte geben Sie eine E-Mail-Adresse ein' },
                        { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' }
                    ]}
                >
                    <Input
                        prefix={<MailOutlined />}
                        placeholder="benutzer@beispiel.de"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onPressEnter={handleEmailSubmit}
                    />
                </Form.Item>
            </Form>
        </div>
    );

    const renderStep1 = () => {
        if (userInTenant) {
            return (
                <div>
                    <Alert
                        message="Benutzer bereits vorhanden"
                        description="Dieser Benutzer ist bereits Teil dieses Mandanten."
                        type="info"
                        showIcon
                    />
                </div>
            );
        }

        if (userExists) {
            return (
                <div>
                    <Title level={4}>Bestehenden Benutzer hinzufügen</Title>
                    <Text>
                        Ein Benutzer mit der E-Mail-Adresse <strong>{email}</strong> existiert bereits im System.
                        Möchten Sie diesen Benutzer {tenantName ? <>zum Mandanten <strong>{tenantName}</strong></> : 'zu Ihrem Mandanten'} hinzufügen?
                    </Text>
                    <div style={{ marginTop: 24 }}>
                        <Button
                            type="primary"
                            onClick={handleAddExistingUser}
                            loading={addExistingUserMutation.isPending}
                            style={{ marginRight: 8 }}
                        >
                            Benutzer hinzufügen
                        </Button>
                        <Button onClick={() => setCurrentStep(0)}>
                            Zurück
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <Title level={4}>Neuen Benutzer erstellen</Title>
                <Text>
                    Ein Benutzer mit der E-Mail-Adresse <strong>{email}</strong> existiert noch nicht.
                    Füllen Sie die folgenden Felder aus, um einen neuen Benutzer zu erstellen.
                </Text>
                <Form
                    form={form}
                    onFinish={handleCreateNewUser}
                    style={{ marginTop: 24 }}
                    layout="vertical"
                >
                    <Form.Item
                        name="displayName"
                        label="Anzeigename"
                        rules={[{ required: true, message: 'Bitte geben Sie einen Anzeigenamen ein' }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Max Mustermann" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Passwort"
                        rules={[
                            { required: true, message: 'Bitte geben Sie ein Passwort ein' },
                            { min: 6, message: 'Das Passwort muss mindestens 6 Zeichen lang sein' }
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Sicheres Passwort" />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={createNewUserMutation.isPending}
                            style={{ marginRight: 8 }}
                        >
                            Benutzer erstellen
                        </Button>
                        <Button onClick={() => setCurrentStep(0)}>
                            Zurück
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        );
    };

    return (
        <Modal
            title={tenantName ? `Benutzer zu ${tenantName} hinzufügen` : 'Benutzer hinzufügen'}
            open={open}
            onCancel={handleClose}
            footer={null}
            width={600}
        >
            <Steps
                current={currentStep}
                style={{ marginBottom: 24 }}
                items={[
                    {
                        title: "E-Mail eingeben"
                    },
                    {
                        title: "Benutzer konfigurieren"
                    }
                ]}
            />
            {currentStep === 0 && (
                <div>
                    {renderStep0()}
                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Button onClick={handleClose} style={{ marginRight: 8 }}>
                            Abbrechen
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleEmailSubmit}
                            loading={checkUserMutation.isPending}
                        >
                            Weiter
                        </Button>
                    </div>
                </div>
            )}

            {currentStep === 1 && renderStep1()}
        </Modal>
    );
}